import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * AI provider status
 */
export interface AIProviderStatus {
  provider: 'claude' | 'openai' | 'ollama';
  available: boolean;
  latencyMs?: number;
  error?: string;
  models?: string[];
}

/**
 * AI status response
 */
export interface AIStatus {
  available: boolean;
  providers: AIProviderStatus[];
  preferredProvider: string | null;
}

/**
 * Get AI status
 */
export async function getAIStatus(): Promise<AIStatus> {
  const response = await apiClient.get<ApiResponse<AIStatus>>('/ai/status');
  return response.data.data as AIStatus;
}

/**
 * AI cost summary
 */
export interface AICostSummary {
  period: { start: string; end: string };
  summary: {
    totalCost: number;
    totalTokens: number;
    requestCount: number;
    byProvider: Array<{ _id: string; cost: number; tokens: number; count: number }>;
    byRequestType: Array<{ _id: string; cost: number; tokens: number; count: number }>;
  };
  budgetUsage: {
    used: number;
    budget: number;
    remaining: number;
    percentUsed: number;
  };
}

/**
 * Get AI cost summary
 */
export async function getAICosts(params?: {
  startDate?: string;
  endDate?: string;
  provider?: string;
}): Promise<AICostSummary> {
  const response = await apiClient.get<ApiResponse<AICostSummary>>('/ai/costs', { params });
  return response.data.data as AICostSummary;
}

/**
 * Code review request
 */
export interface CodeReviewRequest {
  diff: string;
  commitMessage: string;
  filePath?: string;
  language?: string;
  projectRules?: string[];
  provider?: string;
}

/**
 * Code review response
 */
export interface CodeReviewResponse {
  summary: string;
  overallScore: number;
  issues: Array<{
    severity: 'critical' | 'major' | 'minor' | 'suggestion';
    category: string;
    line?: number;
    message: string;
    suggestion?: string;
  }>;
  positives: string[];
  recommendations: string[];
  estimatedComplexity: 'low' | 'medium' | 'high';
}

/**
 * Request code review
 */
export async function reviewCode(request: CodeReviewRequest): Promise<CodeReviewResponse> {
  const response = await apiClient.post<ApiResponse<CodeReviewResponse>>('/ai/review', request);
  return response.data.data as CodeReviewResponse;
}

/**
 * Proxy status
 */
export interface ProxyStatus {
  enabled: boolean;
  configured: boolean;
  host?: string;
  port?: number;
  hasAuth: boolean;
  healthy: boolean;
  latencyMs?: number;
  error?: string;
  testedAt: string;
}

/**
 * Get proxy status
 */
export async function getProxyStatus(): Promise<ProxyStatus> {
  const response = await apiClient.get<ApiResponse<ProxyStatus>>('/ai/proxy/status');
  return response.data.data as ProxyStatus;
}

/**
 * Test proxy connection
 */
export async function testProxy(): Promise<ProxyStatus> {
  const response = await apiClient.post<ApiResponse<ProxyStatus>>('/ai/proxy/test');
  return response.data.data as ProxyStatus;
}

/**
 * AI Cost Dashboard data
 */
export interface AICostDashboard {
  budget: {
    monthly: number;
    used: number;
    remaining: number;
    percentUsed: number;
    syncedFromPortal: boolean;
    calculatedUsage: number;
  };
  periods: {
    today: { cost: number; tokens: number; requests: number };
    thisWeek: { cost: number; tokens: number; requests: number };
    thisMonth: { cost: number; tokens: number; requests: number };
    last30Days: { cost: number; tokens: number; requests: number };
  };
  byProvider: Array<{ _id: string; cost: number; tokens: number; count: number }>;
  byRequestType: Array<{ _id: string; cost: number; tokens: number; count: number }>;
  dailyCosts: Array<{ date: string; cost: number; tokens: number; requests: number }>;
  anthropicSync: {
    available: boolean;
    lastSync: string;
  };
}

/**
 * Get AI cost dashboard data
 */
export async function getAICostDashboard(): Promise<AICostDashboard> {
  const response = await apiClient.get<ApiResponse<AICostDashboard>>('/ai/costs/dashboard');
  return response.data.data as AICostDashboard;
}

/**
 * Update AI budget
 */
export async function updateAIBudget(params: {
  monthlyBudget?: number;
  usedAmount?: number;
}): Promise<{ budgetUsage: AICostDashboard['budget'] }> {
  const response = await apiClient.put<ApiResponse<{ budgetUsage: AICostDashboard['budget'] }>>('/ai/budget', params);
  return response.data.data as { budgetUsage: AICostDashboard['budget'] };
}

/**
 * Sync budget from Anthropic portal
 */
export async function syncFromAnthropic(): Promise<{
  message: string;
  syncedCost?: number;
  budgetUsage: AICostDashboard['budget'];
}> {
  const response = await apiClient.post<ApiResponse<{
    message: string;
    syncedCost?: number;
    budgetUsage: AICostDashboard['budget'];
  }>>('/ai/budget/sync-anthropic');
  return response.data.data as {
    message: string;
    syncedCost?: number;
    budgetUsage: AICostDashboard['budget'];
  };
}

/**
 * Reset to calculated usage
 */
export async function resetToCalculatedUsage(): Promise<{
  message: string;
  budgetUsage: AICostDashboard['budget'];
}> {
  const response = await apiClient.post<ApiResponse<{
    message: string;
    budgetUsage: AICostDashboard['budget'];
  }>>('/ai/budget/sync');
  return response.data.data as {
    message: string;
    budgetUsage: AICostDashboard['budget'];
  };
}
